/**
 * SKI AXIS · /axis-index/cohort1/scorecard/:session_id — Scorecard view
 *
 * Read-only scorecard rendered from the persisted Bronze v0 submission.
 * Cohort-1 confidential. Watermarked. Print-friendly.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, AlertCircle, ShieldCheck, Lock, Calendar, Award, ArrowRight, Printer,
} from 'lucide-react';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import useSEO from '@/hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STYLES = `
.sc-root{background:#FAF7F0;color:#0D1B2A;min-height:100vh;font-family:'DM Sans',-apple-system,sans-serif;}
.sc-shell{max-width:1000px;margin:0 auto;padding:48px 28px;}
@media(max-width:680px){.sc-shell{padding:28px 18px;}}

.sc-watermark{background:#FBF8EE;border:1px dashed rgba(200,168,75,.55);padding:12px 18px;text-align:center;font-size:11px;letter-spacing:.32em;font-weight:700;color:#8A6A14;text-transform:uppercase;margin-bottom:20px;}

.sc-hd{padding:36px 40px;background:linear-gradient(135deg,#0D1B2A 0%,#1A2F4A 100%);color:#FAF7F0;border-left:4px solid #C8A84B;position:relative;overflow:hidden;}
@media(max-width:680px){.sc-hd{padding:28px 22px;}}
.sc-hd::before{content:'';position:absolute;top:-80px;right:-80px;width:280px;height:280px;background:radial-gradient(circle,rgba(200,168,75,.14) 0%,transparent 70%);}
.sc-hd-eb{font-size:10.5px;letter-spacing:.3em;font-weight:700;color:#C8A84B;text-transform:uppercase;margin-bottom:10px;}
.sc-hd-h{font-family:'Cormorant Display',serif;font-size:clamp(28px,4vw,42px);font-weight:700;color:#FAF7F0;line-height:1.1;letter-spacing:-.014em;margin:0 0 8px;}
.sc-hd-h em{font-style:italic;color:#C8A84B;}
.sc-hd-meta{display:flex;flex-wrap:wrap;gap:18px;margin-top:14px;font-size:13px;color:rgba(250,247,240,.7);}
.sc-hd-meta span{display:inline-flex;align-items:center;gap:6px;}
.sc-hd-meta strong{color:#C8A84B;font-weight:600;}

.sc-headline{display:grid;grid-template-columns:1fr 1.4fr;gap:14px;margin:14px 0;}
@media(max-width:780px){.sc-headline{grid-template-columns:1fr;}}
.sc-cert{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:30px 32px;}
.sc-cert-eb{font-size:10.5px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;margin-bottom:8px;}
.sc-cert-num{font-family:'Cormorant Display',serif;font-size:72px;font-weight:700;color:#0D1B2A;line-height:1;letter-spacing:-.025em;margin:6px 0;font-style:italic;}
.sc-cert-num em{color:#C8A84B;font-size:32px;font-style:italic;font-weight:600;letter-spacing:0;vertical-align:super;}
.sc-cert-level{font-family:'Cormorant Display',serif;font-style:italic;font-size:24px;color:#C8A84B;font-weight:700;margin-top:4px;}
.sc-cert-tag{font-size:13px;color:#3D4A5C;margin-top:10px;line-height:1.55;}

.sc-floor{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:30px 32px;}
.sc-floor-eb{font-size:10.5px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;margin-bottom:10px;}
.sc-floor-h{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#0D1B2A;margin:0 0 10px;line-height:1.18;letter-spacing:-.005em;}
.sc-floor-h em{font-style:italic;color:#C8A84B;}
.sc-floor-p{font-size:13.5px;line-height:1.65;color:#3D4A5C;margin:0;}
.sc-floor-p strong{color:#0D1B2A;}
.sc-floor-rule{margin-top:14px;padding:12px 16px;background:#FBF8EE;border-left:3px solid #C8A84B;font-size:13px;color:#0D1B2A;line-height:1.55;}

.sc-dims{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:14px 0;}
@media(max-width:980px){.sc-dims{grid-template-columns:repeat(2,1fr);}}
@media(max-width:560px){.sc-dims{grid-template-columns:1fr;}}
.sc-dim{background:#fff;border:1px solid #E8DFC9;border-top:3px solid #C8A84B;padding:18px 16px;}
.sc-dim-code{font-size:10px;letter-spacing:.22em;font-weight:800;color:#C8A84B;text-transform:uppercase;}
.sc-dim-name{font-family:'Cormorant Display',serif;font-size:16px;font-weight:700;color:#0D1B2A;line-height:1.18;margin:4px 0 8px;letter-spacing:-.005em;}
.sc-dim-score{font-family:'Cormorant Display',serif;font-size:38px;font-weight:700;color:#0D1B2A;line-height:1;letter-spacing:-.018em;font-style:italic;}
.sc-dim-score em{color:#C8A84B;font-style:italic;font-size:18px;font-weight:600;}
.sc-dim-bar{height:4px;background:#FAF0DC;margin-top:10px;overflow:hidden;}
.sc-dim-bar-fill{height:100%;background:linear-gradient(90deg,#C8A84B 0%,#D9BC6A 100%);transition:width .8s ease;}
.sc-dim-level{font-size:10.5px;letter-spacing:.18em;font-weight:700;color:#3D4A5C;text-transform:uppercase;margin-top:8px;}

.sc-next{background:#fff;border:1px solid #E8DFC9;border-left:4px solid #C8A84B;padding:30px 32px;margin-top:14px;}
.sc-next-eb{font-size:10.5px;letter-spacing:.28em;color:#C8A84B;font-weight:800;text-transform:uppercase;margin-bottom:10px;}
.sc-next-h{font-family:'Cormorant Display',serif;font-size:24px;font-weight:700;color:#0D1B2A;margin:0 0 10px;line-height:1.18;letter-spacing:-.005em;}
.sc-next-h em{font-style:italic;color:#C8A84B;}
.sc-next-p{font-size:13.5px;line-height:1.7;color:#3D4A5C;margin:0 0 8px;}
.sc-next-p strong{color:#0D1B2A;}
.sc-next-cta-row{display:flex;gap:12px;margin-top:20px;flex-wrap:wrap;}
.sc-cta{display:inline-flex;align-items:center;gap:10px;background:#0D1B2A;color:#C8A84B;padding:14px 26px;font-size:11.5px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;text-decoration:none;border:1px solid #0D1B2A;cursor:pointer;font-family:inherit;transition:all .15s;}
.sc-cta:hover{background:#C8A84B;color:#0D1B2A;border-color:#C8A84B;}
.sc-cta-2{display:inline-flex;align-items:center;gap:10px;color:#0D1B2A;padding:14px 22px;font-size:11.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;border:1px solid rgba(13,27,42,.25);cursor:pointer;font-family:inherit;background:transparent;}
.sc-cta-2:hover{border-color:#C8A84B;color:#C8A84B;}

@media print {
  .sc-no-print { display: none; }
  .sc-shell { padding: 0; }
}
`;

const niceDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
};

const stageLabel = (tag) => {
  const map = {
    pre_seed: 'Pre-Seed', seed: 'Seed', series_a: 'Series A',
    growth: 'Growth', mature: 'Mature',
  };
  return map[tag] || tag;
};

export default function AxisIndexCohort1ScorecardPage() {
  const { session_id } = useParams();
  useSEO({
    title: 'Bronze Scorecard · The Axis Index™ | SKI Axis',
    description: 'Cohort 1 confidential Bronze scorecard.',
  });
  const [sc, setSc] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/axis-index/cohort1/scorecard/${session_id}`).then((r) => {
      if (live) setSc(r.data);
    }).catch(() => {
      if (live) setErr('Scorecard not found. Check your link or contact your Cohort Lead.');
    });
    return () => { live = false; };
  }, [session_id]);

  if (err) {
    return (
      <div className="sc-root">
        <style>{STYLES}</style>
        <Header />
        <div className="sc-shell" style={{ textAlign: 'center', paddingTop: 90 }}>
          <AlertCircle className="h-8 w-8" style={{ color: '#ad6240', margin: '0 auto' }} />
          <p style={{ marginTop: 12, color: '#3D4A5C', fontSize: 15 }}>{err}</p>
          <Link to="/axis-index/cohort1" className="sc-cta-2" style={{ marginTop: 16, display: 'inline-flex' }}>
            Back to Cohort 1 page
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (!sc) {
    return (
      <div className="sc-root">
        <style>{STYLES}</style>
        <Header />
        <div className="sc-shell" style={{ textAlign: 'center', paddingTop: 120 }}>
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#C8A84B', margin: '0 auto' }} />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="sc-root" data-testid="axis-index-scorecard">
      <style>{STYLES}</style>
      <Header />
      <div className="sc-shell">
        <div className="sc-watermark">{sc.watermark}</div>

        <div className="sc-hd">
          <div className="sc-hd-eb"><Award className="inline h-3 w-3" style={{ marginRight: 6, marginTop: -2 }} /> Bronze Scorecard · The Axis Index™ v1.2</div>
          <h1 className="sc-hd-h">{sc.founder_name}{sc.company ? <span style={{ opacity: .65 }}> · {sc.company}</span> : null}</h1>
          <div className="sc-hd-meta">
            <span><Calendar className="h-3.5 w-3.5" /> Submitted <strong>{niceDate(sc.submitted_at)}</strong></span>
            <span><Lock className="h-3.5 w-3.5" /> {sc.cohort}</span>
            <span>Stage Tag · <strong>{stageLabel(sc.stage_tag)}</strong> · locked</span>
            <span>Instrument · <strong>{sc.instrument_version}</strong></span>
          </div>
        </div>

        <div className="sc-headline">
          <div className="sc-cert" data-testid="sc-cert">
            <div className="sc-cert-eb">Certified Level · Floor Rule applied</div>
            <div className="sc-cert-num">{sc.certified_level.code}<em>· {sc.certified_level.lower}–{sc.certified_level.upper}</em></div>
            <div className="sc-cert-level">{sc.certified_level.name}</div>
            <p className="sc-cert-tag">
              Certification reflects the lowest-scoring dimension. Composite average is informational only.
            </p>
          </div>
          <div className="sc-floor">
            <div className="sc-floor-eb">Composite & Floor Rule</div>
            <h2 className="sc-floor-h">Composite <em>{sc.composite_score}</em> · pre-floor level {sc.composite_level_pre_floor.code}</h2>
            <p className="sc-floor-p">
              Your composite (average across all five dimensions) reads as <strong>Level {sc.composite_level_pre_floor.code} ({sc.composite_level_pre_floor.name})</strong>.
              {sc.floor_rule_applied
                ? <> The Floor Rule lowers your <strong>certified</strong> level to <strong>{sc.certified_level.code} ({sc.certified_level.name})</strong> because at least one dimension scored below {sc.composite_level_pre_floor.lower}.</>
                : <> No Floor Rule adjustment — every dimension is at or above the lower bound of Level {sc.composite_level_pre_floor.code}.</>}
            </p>
            {sc.floor_rule_applied && (
              <div className="sc-floor-rule">
                <strong>Weakest dimension scored {sc.weakest_dimension_score}.</strong> Lift it above {sc.composite_level_pre_floor.lower}, hold the others steady, and your certified level rises to {sc.composite_level_pre_floor.code}.
              </div>
            )}
          </div>
        </div>

        <div className="sc-dims" data-testid="sc-dimensions">
          {Object.entries(sc.dimension_scores).map(([code, dim]) => (
            <div key={code} className="sc-dim" data-testid={`sc-dim-${code}`}>
              <div className="sc-dim-code">{code}</div>
              <div className="sc-dim-name">{dim.name}</div>
              <div className="sc-dim-score">{dim.score}<em>/100</em></div>
              <div className="sc-dim-bar"><div className="sc-dim-bar-fill" style={{ width: `${dim.score}%` }} /></div>
              <div className="sc-dim-level">{dim.level.code} · {dim.level.name}</div>
            </div>
          ))}
        </div>

        <div className="sc-next">
          <div className="sc-next-eb">What happens next</div>
          <h2 className="sc-next-h">Your scorecard is on <em>file</em>.</h2>
          <p className="sc-next-p">
            This is your <strong>baseline</strong>. You will be re-indexed at Week 12 (graduation) — that score is the headline of the 2027 Annual Report's Track B narrative.
          </p>
          <p className="sc-next-p">
            Between now and then, your Cohort Lead will work with you on the dimensions where you have the most room to move. Your improvement delta is yours; nothing is published before October 2026 without your explicit written consent.
          </p>
          <div className="sc-next-cta-row sc-no-print">
            <button className="sc-cta" onClick={() => window.print()} data-testid="sc-print">
              <Printer className="h-4 w-4" /> Save as PDF
            </button>
            <Link className="sc-cta-2" to="/axis-index">
              Read the v1.2 Framework <ArrowRight className="h-4 w-4" />
            </Link>
            <Link className="sc-cta-2" to="/axis-index/cohort1">
              <ShieldCheck className="h-4 w-4" /> Cohort 1 home
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
